import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Platform,
  RefreshControl,
  Pressable,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import UserAvatar from '../../components/shared/UserAvatar';
import { Button } from '../../components';
import { palette } from '../../utils/palette';
import type { BlogArticle } from '../../data/blogArticles';

export interface AchievementItem {
  id: string;
  name: string;
  icon: 'color-palette' | 'shirt' | 'sparkles' | 'library';
  color: string;
  route: string;
  completed: boolean;
  onPress: () => void;
}

export interface FeatureCardConfig {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  gradient: [string, string];
  bgGradient: [string, string];
  accentColor: string;
  route: string;
  features: string[];
  story: string;
  difficulty: string;
  timeEstimate: string;
}

export type TourHighlightTarget = 'badges' | 'color-analysis' | null;

export interface TourHighlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ModernHomeContentProps {
  colors: Record<string, string>;
  firstName: string;
  welcomeMessage: string;
  hasCompletedAnalysis: boolean;
  recommendedPalette: { name: string; colors: string[] } | null;
  user: {
    id: string;
    name?: string;
    email?: string;
    profileImageUrl?: string;
  } | null;
  onViewPalette: () => void;
  onProfile: () => void;
  onSettings: () => void;
  journeyTitle: string;
  achievements: AchievementItem[];
  featureCards: FeatureCardConfig[];
  onFeaturePress: (route: string) => void;
  getFeatureButtonLabel: (featureId: string) => string;
  discoverArticles: BlogArticle[];
  onArticlePress: (articleId: string) => void;
  onViewAllArticles: () => void;
  showUpgradeCta: boolean;
  onUpgradePress: () => void;
  isRefreshing: boolean;
  onRefresh: () => void;
  colorAnalysisBadgeRef: React.RefObject<View>;
  journeyCardRef: React.RefObject<View>;
  badgesRowRef: React.RefObject<View>;
  tourHighlightTarget?: TourHighlightTarget;
  onTourHighlightRect?: (rect: TourHighlightRect | null) => void;
  isMobile?: boolean;
}

const TOUR_SPOTLIGHT_PADDING = 12;
const MOBILE_TAB_BAR_HEIGHT = 65;
const MOBILE_TAB_BAR_EXTRA = 10;

const MODERN_HERO_GRADIENT: [string, string] = ['#6366F1', '#8B5CF6'];
const MODERN_HERO_SUBTLE: [string, string] = [
  'rgba(99,102,241,0.08)',
  'rgba(139,92,246,0.04)',
];

const ModernHomeContent: React.FC<ModernHomeContentProps> = ({
  colors,
  firstName,
  welcomeMessage,
  hasCompletedAnalysis,
  recommendedPalette,
  user,
  onViewPalette,
  onProfile,
  onSettings: _onSettings,
  journeyTitle,
  achievements,
  featureCards,
  onFeaturePress,
  getFeatureButtonLabel,
  discoverArticles,
  onArticlePress,
  onViewAllArticles,
  showUpgradeCta,
  onUpgradePress,
  isRefreshing,
  onRefresh,
  colorAnalysisBadgeRef,
  journeyCardRef,
  badgesRowRef,
  tourHighlightTarget = null,
  onTourHighlightRect,
}) => {
  const { isMobile } = useResponsiveLayout();
  const insets = useSafeAreaInsets();
  const scrollTopPadding = isMobile ? insets.top + 12 : 16;
  const scrollViewRef = React.useRef<ScrollView>(null);
  const scrollContentRef = React.useRef<View>(null);
  const journeySectionRef = React.useRef<View>(null);
  const [hoveredCard, setHoveredCard] = React.useState<string | null>(null);
  const [hoveredArticle, setHoveredArticle] = React.useState<string | null>(
    null
  );

  const getTourTargetRef = React.useCallback((): React.RefObject<View> | null => {
    if (tourHighlightTarget === 'badges') return journeySectionRef;
    if (tourHighlightTarget === 'color-analysis') return colorAnalysisBadgeRef;
    return null;
  }, [tourHighlightTarget, colorAnalysisBadgeRef]);

  const measureTourHighlight = React.useCallback(() => {
    const targetRef = getTourTargetRef();
    if (!targetRef?.current || !onTourHighlightRect) return;

    targetRef.current.measureInWindow((x, y, width, height) => {
      if (width <= 0 || height <= 0) return;
      const pad = TOUR_SPOTLIGHT_PADDING;
      onTourHighlightRect({
        x: Math.max(0, x - pad),
        y: Math.max(0, y - pad),
        width: width + pad * 2,
        height: height + pad * 2,
      });
    });
  }, [getTourTargetRef, onTourHighlightRect]);

  const scrollTourTargetIntoView = React.useCallback(() => {
    if (!scrollViewRef.current) {
      measureTourHighlight();
      return;
    }

    const finishScroll = () => {
      requestAnimationFrame(() => {
        setTimeout(measureTourHighlight, 80);
      });
    };

    // Keep natural homepage scroll (hero + welcome card) instead of pinning badges to the top.
    if (tourHighlightTarget === 'badges' || tourHighlightTarget === 'color-analysis') {
      if (!journeySectionRef.current || !scrollContentRef.current) {
        scrollViewRef.current.scrollTo({ y: 0, animated: false });
        finishScroll();
        return;
      }

      journeySectionRef.current.measureLayout(
        scrollContentRef.current,
        (_left, top, _width, height) => {
          let scrollY = 0;

          if (isMobile) {
            const windowHeight = Dimensions.get('window').height;
            const tabBarClearance =
              MOBILE_TAB_BAR_HEIGHT + insets.bottom + MOBILE_TAB_BAR_EXTRA;
            const tooltipReserve = tourHighlightTarget === 'badges' ? 180 : 100;
            const visibleBottom = windowHeight - tabBarClearance - tooltipReserve;
            const sectionBottom = top + height;

            if (sectionBottom > visibleBottom) {
              scrollY = Math.max(0, sectionBottom - visibleBottom + 16);
            }
          }

          scrollViewRef.current?.scrollTo({ y: scrollY, animated: false });
          finishScroll();
        },
        () => {
          scrollViewRef.current?.scrollTo({ y: 0, animated: false });
          finishScroll();
        }
      );
      return;
    }

    measureTourHighlight();
  }, [tourHighlightTarget, measureTourHighlight, isMobile, insets.bottom]);

  React.useEffect(() => {
    if (!tourHighlightTarget) {
      onTourHighlightRect?.(null);
      return;
    }

    const timer = setTimeout(scrollTourTargetIntoView, 120);
    return () => clearTimeout(timer);
  }, [tourHighlightTarget, scrollTourTargetIntoView, onTourHighlightRect]);

  const content = (
    <>
      {/* Hero */}
      <View
        style={[
          styles.heroWrap,
          isMobile && styles.heroWrapMobile,
          { backgroundColor: colors.background },
        ]}
      >
        <LinearGradient
          colors={MODERN_HERO_SUBTLE}
          style={StyleSheet.absoluteFill}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
        />
        <Text style={[styles.heroEyebrow, { color: colors.primary }]}>
          ColourSense
        </Text>
        <Text style={[styles.heroTitle, { color: colors.text }]}>
          Your real-time colours
        </Text>
        <Text style={[styles.heroSubtitle, { color: colors.secondary }]}>
          AI-powered analysis for your skin, hair & eyes — get a personal
          palette and seasonal recommendations in minutes.
        </Text>
      </View>

      {/* Welcome + journey card (full width) */}
      <View
        ref={journeyCardRef}
        collapsable={false}
        style={[styles.welcomeCard, { backgroundColor: colors.card }]}
      >
        <View style={styles.welcomeCardTop}>
          <TouchableOpacity onPress={onProfile} activeOpacity={0.8}>
            <UserAvatar
              size={isMobile ? 56 : 64}
              imageUrl={user?.profileImageUrl}
              name={user?.name}
              email={user?.email}
              showActiveStatus
              isActive
            />
          </TouchableOpacity>
          <View style={styles.welcomeCardText}>
            <Text style={[styles.welcomeGreeting, { color: colors.text }]}>
              Hi, {firstName}
            </Text>
            {welcomeMessage ? (
              <Text
                style={[styles.welcomeMessage, { color: colors.secondary }]}
                numberOfLines={2}
              >
                {welcomeMessage}
              </Text>
            ) : (
              <Text
                style={[styles.welcomeMessage, { color: colors.secondary }]}
              >
                Start your colour journey with a quick analysis.
              </Text>
            )}
          </View>
        </View>
        {hasCompletedAnalysis && recommendedPalette?.colors?.length ? (
          <View
            style={[
              styles.paletteRow,
              isMobile && styles.paletteRowMobile,
              { borderTopColor: colors.border },
            ]}
          >
            <View
              style={[
                styles.paletteSwatches,
                isMobile && styles.paletteSwatchesMobile,
              ]}
            >
              {recommendedPalette.colors.slice(0, 8).map((hex, i) => (
                <View
                  key={i}
                  style={[
                    styles.paletteSwatch,
                    isMobile && styles.paletteSwatchMobile,
                    { backgroundColor: hex },
                  ]}
                />
              ))}
            </View>
            <Button
              title="View my palette"
              variant="soft"
              onPress={onViewPalette}
              icon={
                <Ionicons
                  name="arrow-forward"
                  size={16}
                  color={palette.outlinePurple}
                />
              }
              iconPosition="right"
              style={[
                styles.viewPaletteBtn,
                isMobile && styles.viewPaletteBtnMobile,
              ]}
            />
          </View>
        ) : (
          <TouchableOpacity
            style={[
              styles.ctaAnalysisBlock,
              { backgroundColor: colors.primary + '12' },
            ]}
            onPress={() => onFeaturePress('ColourAnalysis')}
            activeOpacity={0.8}
          >
            <Ionicons name="color-wand" size={24} color={colors.primary} />
            <Text style={[styles.ctaAnalysisText, { color: colors.primary }]}>
              Discover your palette — free analysis
            </Text>
            <Ionicons name="arrow-forward" size={18} color={colors.primary} />
          </TouchableOpacity>
        )}
        <View
          ref={journeySectionRef}
          collapsable={false}
          onLayout={() => {
            if (tourHighlightTarget === 'badges') {
              measureTourHighlight();
            }
          }}
        >
          <View style={styles.journeyCardHeader}>
            <Ionicons name="rocket-outline" size={22} color={colors.primary} />
            <Text style={[styles.journeyCardTitle, { color: colors.text }]}>
              {journeyTitle}
            </Text>
          </View>
          <Text style={[styles.journeyCardDesc, { color: colors.secondary }]}>
            Unlock your colour profile with analysis, virtual drape, and palette
            tools.
          </Text>
          <View
            ref={badgesRowRef}
            collapsable={false}
            style={[styles.badgesRow, isMobile && styles.badgesRowMobile]}
          >
            {achievements.map((a) => (
              <View
                key={a.id}
                ref={
                  a.id === 'color-analysis' ? colorAnalysisBadgeRef : undefined
                }
                collapsable={a.id !== 'color-analysis'}
                style={isMobile ? styles.badgeItemMobile : undefined}
                onLayout={() => {
                  if (
                    tourHighlightTarget === 'color-analysis' &&
                    a.id === 'color-analysis'
                  ) {
                    measureTourHighlight();
                  }
                }}
              >
                <TouchableOpacity
                  style={[
                    styles.badgeBtn,
                    isMobile && styles.badgeBtnMobile,
                    {
                      backgroundColor: a.completed
                        ? a.color + '20'
                        : colors.background,
                    },
                    a.completed && styles.badgeBtnCompleted,
                  ]}
                  onPress={a.onPress}
                  activeOpacity={0.8}
                >
                  <View
                    style={[
                      styles.badgeIconWrap,
                      { backgroundColor: a.color + '30' },
                    ]}
                  >
                    <Ionicons
                      name={a.icon as keyof typeof Ionicons.glyphMap}
                      size={18}
                      color={a.color}
                    />
                  </View>
                  <Text
                    style={[
                      styles.badgeLabel,
                      { color: a.completed ? colors.text : colors.secondary },
                    ]}
                    numberOfLines={1}
                  >
                    {a.name}
                  </Text>
                  {a.completed && (
                    <Ionicons
                      name="checkmark-circle"
                      size={16}
                      color={a.color}
                    />
                  )}
                </TouchableOpacity>
              </View>
            ))}
          </View>
        </View>
      </View>

      {/* Feature cards — below journey */}
      <View style={styles.featureCardsRow}>
        {featureCards.map((card) => (
          <TouchableOpacity
            key={card.id}
            style={[
              styles.featureCard,
              { backgroundColor: colors.card },
              hoveredCard === card.id && styles.featureCardHover,
            ]}
            onPress={() => onFeaturePress(card.route)}
            activeOpacity={0.95}
            onPressIn={() => setHoveredCard(card.id)}
            onPressOut={() => setHoveredCard(null)}
            {...(Platform.OS === 'web' && {
              onMouseEnter: () => setHoveredCard(card.id),
              onMouseLeave: () => setHoveredCard(null),
            })}
          >
            <LinearGradient
              colors={card.bgGradient}
              style={StyleSheet.absoluteFill}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
            />
            <View style={styles.featureCardContent}>
              <View style={styles.featureCardHeader}>
                <LinearGradient
                  colors={card.gradient}
                  style={styles.featureCardIcon}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                >
                  <Ionicons
                    name={card.icon as keyof typeof Ionicons.glyphMap}
                    size={24}
                    color="#fff"
                  />
                </LinearGradient>
                <View style={styles.featureCardMeta}>
                  <Text
                    style={[
                      styles.featureCardDifficulty,
                      { color: card.accentColor },
                    ]}
                  >
                    {card.difficulty}
                  </Text>
                  <Text
                    style={[
                      styles.featureCardTime,
                      { color: colors.secondary },
                    ]}
                  >
                    {card.timeEstimate}
                  </Text>
                </View>
              </View>
              <Text style={[styles.featureCardTitle, { color: colors.text }]}>
                {card.title}
              </Text>
              <Text
                style={[
                  styles.featureCardSubtitle,
                  { color: card.accentColor },
                ]}
              >
                {card.subtitle}
              </Text>
              <Text
                style={[styles.featureCardDesc, { color: colors.secondary }]}
                numberOfLines={2}
              >
                {card.description}
              </Text>
              <Button
                title={getFeatureButtonLabel(card.id)}
                onPress={() => onFeaturePress(card.route)}
                variant="primary"
                style={[
                  styles.featureCardCta,
                  { backgroundColor: card.accentColor },
                ]}
                textStyle={{ color: '#fff', fontWeight: '600' }}
                icon={<Ionicons name="arrow-forward" size={16} color="#fff" />}
                iconPosition="right"
              />
            </View>
          </TouchableOpacity>
        ))}
      </View>

      {/* Discover & Learn */}
      {discoverArticles.length > 0 && (
        <View
          style={[
            styles.discoverSection,
            { backgroundColor: colors.background },
          ]}
        >
          <View style={styles.discoverHeader}>
            <Ionicons name="book-outline" size={20} color={colors.primary} />
            <Text style={[styles.discoverTitle, { color: colors.text }]}>
              Discover & learn
            </Text>
          </View>
          <View style={styles.articlesRow}>
            {discoverArticles.slice(0, 3).map((article) => (
              <TouchableOpacity
                key={article.id}
                style={[
                  styles.articleCard,
                  { backgroundColor: colors.card },
                  hoveredArticle === article.id && styles.articleCardHover,
                ]}
                onPress={() => onArticlePress(article.id)}
                activeOpacity={0.9}
                {...(Platform.OS === 'web' && {
                  onMouseEnter: () => setHoveredArticle(article.id),
                  onMouseLeave: () => setHoveredArticle(null),
                })}
              >
                {article.image ? (
                  <Image
                    source={{ uri: article.image }}
                    style={styles.articleImage}
                    resizeMode="cover"
                  />
                ) : (
                  <View
                    style={[
                      styles.articleImagePlaceholder,
                      { backgroundColor: colors.border },
                    ]}
                  >
                    <Ionicons
                      name="document-text"
                      size={28}
                      color={colors.secondary}
                    />
                  </View>
                )}
                <View style={styles.articleBody}>
                  <Text
                    style={[styles.articleCategory, { color: colors.primary }]}
                  >
                    {article.category}
                  </Text>
                  <Text
                    style={[styles.articleTitle, { color: colors.text }]}
                    numberOfLines={2}
                  >
                    {article.title}
                  </Text>
                  <Text
                    style={[styles.articleMeta, { color: colors.secondary }]}
                  >
                    {article.readTime} min read
                  </Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
          <TouchableOpacity
            style={styles.viewAllArticles}
            onPress={onViewAllArticles}
          >
            <Text
              style={[styles.viewAllArticlesText, { color: colors.primary }]}
            >
              Read all articles
            </Text>
            <Ionicons name="arrow-forward" size={18} color={colors.primary} />
          </TouchableOpacity>
        </View>
      )}

      {/* Upgrade CTA */}
      {showUpgradeCta && (
        <Pressable
          style={[
            styles.upgradeCta,
            { backgroundColor: colors.primary + '14' },
          ]}
          onPress={onUpgradePress}
        >
          <LinearGradient
            colors={MODERN_HERO_GRADIENT}
            style={styles.upgradeCtaGradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
          >
            <View style={styles.upgradeCtaContent}>
              <Text style={styles.upgradeCtaTitle}>
                Unlock your full palette
              </Text>
              <Text style={styles.upgradeCtaSub}>
                Unlimited analyses, virtual drape & wardrobe — from $4.99/mo
              </Text>
              <View style={styles.upgradeCtaBtnWrap}>
                <Text style={styles.upgradeCtaBtnText}>View plans</Text>
                <Ionicons name="arrow-forward" size={18} color="#fff" />
              </View>
            </View>
          </LinearGradient>
        </Pressable>
      )}
    </>
  );

  if (isMobile) {
    return (
      <ScrollView
        ref={scrollViewRef}
        style={styles.scroll}
        contentContainerStyle={[
          styles.scrollContent,
          styles.scrollContentMobile,
          { paddingTop: scrollTopPadding },
        ]}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} />
        }
      >
        <View ref={scrollContentRef} collapsable={false}>
          {content}
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView
      ref={scrollViewRef}
      style={styles.scroll}
      contentContainerStyle={[
        styles.scrollContent,
        { paddingTop: scrollTopPadding },
      ]}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} />
      }
    >
      <View ref={scrollContentRef} collapsable={false}>
        {content}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: {
    paddingHorizontal: 16,
    paddingBottom: 48,
    maxWidth: 1200,
    width: '100%',
    alignSelf: 'center',
  },
  scrollContentMobile: {
    paddingBottom: 100,
  },
  heroWrap: {
    borderRadius: 12,
    paddingTop: 16,
    paddingBottom: 12,
    paddingHorizontal: 16,
    marginBottom: 8,
    overflow: 'hidden',
  },
  heroWrapMobile: {
    paddingTop: 20,
  },
  heroEyebrow: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  heroTitle: {
    fontSize: 22,
    fontWeight: '800',
    letterSpacing: -0.4,
    lineHeight: 28,
    marginBottom: 4,
  },
  heroSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    opacity: 0.9,
    maxWidth: 520,
  },
  featureCardsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 10,
  },
  welcomeCard: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }),
    ...(Platform.OS !== 'web' && {
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 3,
    }),
  },
  welcomeCardTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  welcomeCardText: {
    flex: 1,
    minWidth: 0,
  },
  welcomeGreeting: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 2,
  },
  welcomeMessage: {
    fontSize: 14,
    lineHeight: 20,
  },
  paletteRow: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 10,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
  },
  paletteRowMobile: {
    marginTop: 10,
    paddingTop: 10,
    gap: 8,
  },
  paletteSwatches: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 6,
    alignSelf: 'stretch',
  },
  paletteSwatchesMobile: {
    gap: 4,
  },
  paletteSwatch: {
    width: 32,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
    flexShrink: 0,
  },
  paletteSwatchMobile: {
    width: 28,
    height: 28,
    borderRadius: 6,
  },
  viewPaletteBtn: {
    alignSelf: 'stretch',
  },
  viewPaletteBtnMobile: {
    width: '100%',
  },
  ctaAnalysisBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 16,
    padding: 14,
    borderRadius: 12,
  },
  ctaAnalysisText: {
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
  },
  journeyCard: {
    borderRadius: 16,
    padding: 20,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }),
    ...(Platform.OS !== 'web' && {
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 3,
    }),
  },
  journeyCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.06)',
    marginBottom: 6,
  },
  journeyCardTitle: {
    fontSize: 17,
    fontWeight: '700',
  },
  journeyCardDesc: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 10,
  },
  badgesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  badgesRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    flexWrap: 'nowrap',
  },
  badgeItemMobile: {
    alignSelf: 'stretch',
    width: '100%',
  },
  badgeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 12,
  },
  badgeBtnMobile: {
    width: '100%',
  },
  badgeBtnCompleted: {},
  badgeIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeLabel: {
    fontSize: 13,
    fontWeight: '600',
    flex: 1,
  },
  featureCard: {
    flex: 1,
    minWidth: 240,
    borderRadius: 16,
    padding: 18,
    marginBottom: 0,
    overflow: 'hidden',
    position: 'relative',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      cursor: 'pointer',
    }),
    ...(Platform.OS !== 'web' && {
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 3,
    }),
  },
  featureCardHover: {
    ...(Platform.OS === 'web' && {
      transform: [{ scale: 1.02 }],
      boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
    }),
  },
  featureCardContent: {
    position: 'relative',
    zIndex: 1,
  },
  featureCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  featureCardIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  featureCardMeta: {
    alignItems: 'flex-end',
  },
  featureCardDifficulty: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  featureCardTime: {
    fontSize: 12,
  },
  featureCardTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 2,
  },
  featureCardSubtitle: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 6,
  },
  featureCardDesc: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 12,
  },
  featureCardCta: {
    alignSelf: 'flex-start',
  },
  discoverSection: {
    marginTop: 28,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.06)',
  },
  discoverHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
  },
  discoverTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  articlesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  articleCard: {
    flex: 1,
    minWidth: 160,
    maxWidth: 280,
    borderRadius: 12,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  articleCardHover: {
    ...(Platform.OS === 'web' && {
      opacity: 0.95,
      transform: [{ translateY: -2 }],
    }),
  },
  articleImage: {
    width: '100%',
    height: 100,
    backgroundColor: palette.lightGray,
  },
  articleImagePlaceholder: {
    width: '100%',
    height: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  articleBody: {
    padding: 12,
  },
  articleCategory: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  articleTitle: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 20,
  },
  articleMeta: {
    fontSize: 12,
    marginTop: 4,
  },
  viewAllArticles: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 16,
  },
  viewAllArticlesText: {
    fontSize: 14,
    fontWeight: '600',
  },
  upgradeCta: {
    marginTop: 32,
    borderRadius: 16,
    overflow: 'hidden',
  },
  upgradeCtaGradient: {
    paddingVertical: 20,
    paddingHorizontal: 24,
  },
  upgradeCtaContent: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
  },
  upgradeCtaTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#fff',
    flex: 1,
    minWidth: 200,
  },
  upgradeCtaSub: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.9)',
    flex: 1,
    minWidth: 200,
  },
  upgradeCtaBtnWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 10,
  },
  upgradeCtaBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
});

export default ModernHomeContent;
